import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faPen } from '@fortawesome/free-solid-svg-icons';

import { Grid, Typography, Tooltip, IconButton } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import Paper from '@material-ui/core/Paper';
import Filter from '../../../components/Filter/components';
import FilterMobile from '../../../components/FilterMobile/components';
import Button from '../../../components/Button';
import MTable from '../../../components/MTable';
import TableMobile from '../../../components/TableMobile';
import CategoriesRegistration from './CategoriesRegistration';

import { formatFilters } from '../../../helpers/formatters';
import { loadCategories } from '../store/thunk';
import { loadFilters } from '../../../components/Filter/store/thunk';
import { handleRegistration, handleUpdate } from '../store/actions';

import useWindowDimensions from '../../../helpers/hooks/useWindowDimensions';
import useDidMount from '../../../helpers/hooks/useDidMount';
import { convertList } from '../../../helpers/converters';

import './Categories.scss';

const headers = [
  { title: 'Descrição', field: 'categoryName', sortable: false },
  { title: 'Tipo de lançamento', field: 'transactionType', sortable: false },
  { title: 'Centro de Custo', field: 'costCenter', sortable: false }
];

const width = window.innerWidth;
const tableRowWidth = width < 768 ? 120 : 150;

export default function Categories() {
  const dispatch = useDispatch();

  const { width } = useWindowDimensions();
  const isMobile = width < 960;

  const didComponentMount = useDidMount();

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { list: fetchList, total, isLoading, registration } = useSelector(state => state.categories);
  const { selects, qp } = useSelector(state => state.filters);

  const filters = formatFilters(['transactionType', 'costCenter', 'status'], selects);
  const list = convertList(['transactionType', 'costCenter'], filters, fetchList);

  const [categoriesPage, setCategoriesPage] = useState(1);
  const categoriesPageSize = 10;
  const categoriesPageCount = Math.ceil(total / categoriesPageSize);

  const requestLoadCategories = ({ page, qp }) => {
    dispatch(loadCategories({ userId, establishmentId, page, qp }));
  }

  useEffect(() => {
    const getData = async () => {
      await requestLoadCategories({ page: 1, qp: '' });
      await dispatch(loadFilters(userId, establishmentId));
    }

    if(establishmentId) {
      getData();
    }
  }, [establishmentId]);

  useEffect(() => {
    if(establishmentId && !didComponentMount) {
      requestLoadCategories({ page: categoriesPage, qp });
    }
  }, [categoriesPage]);

  const handleRegister = () => {
    dispatch(handleRegistration(true));
  }

  const onUpdate = category => {
    dispatch(handleUpdate(category));
  }

  return (
    <>
      {registration && (
        <CategoriesRegistration isOpen={registration} />
      )}
      {isMobile && (
        <Grid container xs={12} style={{ marginBottom: 10 }}>
          <Button
            type="button"
            color="success"
            startIcon={
              <FontAwesomeIcon
                icon={faPlusCircle}
                color="#FFFFFF"
                size="xs"
              />
            }
            onClick={handleRegister}
          >
            Cadastrar Categoria
          </Button>
        </Grid>
      )}
      <Paper>
        {isMobile ? (
          <FilterMobile
            filter
            hasSearchTerm
            searchTermPlaceholder="Descrição"
            filters={filters}
            handlePageRequest={requestLoadCategories}
          />
        ) : (
          <Filter
            filter
            hasSearchTerm
            filters={filters}
            handlePageRequest={requestLoadCategories}
            actionButton={
              <Button
                type="button"
                color="primary"
                startIcon={
                  <FontAwesomeIcon
                    icon={faPlusCircle}
                    color="#FFFFFF"
                    size="xs"
                  />
                }
                onClick={handleRegister}
              >
                Adicionar Categoria
              </Button>
            }
          />
        )}
        {isMobile && (
          <TableMobile
            loading={isLoading}
            data={list?.map(item => {
              return {
                ...item,
                categoryNameItem: {
                  index: 1,
                  label: 'Descrição',
                  value: <Typography color='textSecondary' variant='body2' style={{ wordBreak: 'break-all' }}>{item?.categoryName}</Typography>
                },
                transactionTypeItem: {
                  index: 2,
                  label: 'Tipo de lançamento',
                  value: <Typography color='textSecondary' variant='body2' style={{ wordBreak: 'break-all' }}>{item?.transactionType}</Typography>
                },
                costCenterItem: {
                  index: 3,
                  label: 'Centro de Custo',
                  value: <Typography color='textSecondary' variant='body2' style={{ wordBreak: 'break-all' }}>{item?.costCenter}</Typography>
                }
              }
            })}
            actions={[
              {
                action: rowData => {
                  const data = { ...rowData };

                  delete data['categoryNameItem'];
                  delete data['transactionTypeItem'];
                  delete data['costCenterItem'];

                  return(
                    <Tooltip placement='top' title='Editar' enterTouchDelay={0}>
                      <IconButton onClick={() => onUpdate(data)}>
                        <FontAwesomeIcon
                          icon={faPen}
                          color="#022A5C"
                          size="xs"
                        />
                      </IconButton>
                    </Tooltip>
                  );
                }
              }
            ]}
          />
        )}
        {!isMobile && (
          <MTable
            loading={isLoading}
            headers={headers}
            data={list}
            actions={[
              rowData => ({
                icon: () => (
                  <FontAwesomeIcon
                    icon={faPen}
                    color="#022A5C"
                    size="xs"
                  />
                ),
                tooltip: 'Editar',
                onClick: (event, rowData) => onUpdate(rowData)
              })
            ]}
            actionColumnWidth={tableRowWidth}
          />
        )}
        <Grid
          container
          xs={12}
          style={{ padding: 10, display: 'flex', justifyContent: 'flex-end' }}
        >
          <Pagination
            color="primary"
            variant="outlined"
            shape="rounded"
            count={categoriesPageCount}
            page={categoriesPage}
            onChange={(_, page) => setCategoriesPage(page)}
          />
        </Grid>
      </Paper>
    </>
  );
}